<template>
    <div class="h-full w-full flex flex-col">
        <import-format-selector v-model="importFormat" class="w-fit my-2" @update:model-value="convertCsvToGridRows"/>
        <import-format-account-configuration
            v-if="[CsvFormats.VIPPS,CsvFormats.KLARNA].includes(importFormat)"
            v-model="importConfig"
            :csv-format="importFormat"
        />
        <ag-grid-vue
            class="ag-grid-theme-alpine w-full h-full grow"
            :theme="agTheme"
            :column-defs="columnDefs"
            :grid-options="gridOptions"
            :row-data="rowData"
            grand-total-row="bottom"
        />
        <div class="flex justify-between">
            <UButton color="neutral" variant="outline" @click="emit('close', true)">Avbryt</UButton>
            <UButton color="primary" @click="readLines">Neste</UButton>
        </div>
    </div>
</template>

<script setup lang="ts">
/*
TODO: Maintaining OCP, we should make an interface for a type matcher, and register matchers
 */
import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {computed, onMounted, ref, watch} from 'vue'
import Papa from 'papaparse'
import {GridApi, GridOptions, GridReadyEvent, ICellRendererParams, IMenuActionParams, ValueGetterParams} from 'ag-grid-community'
import ImportFormatSelector from '@/csv-transaction-importer/ImportFormatSelector.vue'
import {CsvFormats, customRowMapper, defaultAccountConfig, EntryLineFields, ImportAccountConfiguration, klarnaMapper, vippsMapper} from '@/csv-transaction-importer/csv-import.service'
import {formatCurrency} from '@/services/formatter.service'
import ImportFormatAccountConfiguration from '@/csv-transaction-importer/ImportFormatAccountConfiguration.vue'
import {writeFieldListToTransactionEntries} from '@/csv-transaction-importer/fieldsToTransEntryConverter'

const props = defineProps({
    columns: {
        type: Array<string>,
        required: true
    },
    data: {
        type: String
    }
})

const emit = defineEmits(['close'])

const rowData = ref<EntryLineFields[]>([])
const gridApi = ref<GridApi>()
const importFormat = ref<CsvFormats>(CsvFormats.CUSTOM)
const importConfig = ref<ImportAccountConfiguration>(defaultAccountConfig())

const gridOptions: GridOptions = {
    cellSelection: true,
    getContextMenuItems: () => {
        return [{
            name: 'Slett rad',
            action: (params: IMenuActionParams) => {
                const rowIndex = params.node?.rowIndex
                if (rowIndex === null || rowIndex === undefined)
                    return
                rowData.value.splice(rowIndex, 1)
            }
        }]
    },
    onGridReady(event
                :
                GridReadyEvent
    ) {
        gridApi.value = event.api
        window.gridApi = event.api
    }
    ,
    maintainColumnOrder: true,
    aggFuncs:
        {
            balanceSum: (params) => {
                return params.values.reduce((prev, next) => {
                    return prev + next
                }, 0)
            }
        }
    ,
    defaultColDef: {
        editable: true
    }
}

const columnDefs = computed(() => {
    return [
        {
            headerName: 'Dato',
            field: 'date',
            width: 100
        },
        {
            headerName: 'Debet',
            field: 'debitAccount'
        },
        {
            headerName: 'Debet MVA',
            field: 'debitVat'
        },
        {
            headerName: 'Kredit',
            field: 'creditAccount'
        },
        {
            headerName: 'Kredit MVA',
            field: 'creditVat'
        },
        {
            headerName: 'Beskrivelse',
            field: 'description'
        },
        {
            headerName: 'Prosjekt',
            field: 'project'
        },
        {
            headerName: 'Avdeling',
            field: 'department'
        },
        {
            headerName: 'Beløp',
            field: 'amount',
            cellClass: 'text-right',
            valueGetter: (params: ValueGetterParams) => {
                if (!params.data || !params.data.amount)
                    return
                return Number(params.data.amount.replaceAll(',', '.'))
            },
            cellRenderer: (params: ICellRendererParams) => formatCurrency(params.value)
        },
        {
            headerName: 'Linjesum',
            valueGetter: ({data}: ValueGetterParams) => {
                if (!data) return
                const debit = Number(data.debitAccount)
                const credit = Number(data.creditAccount)
                if (credit && debit) {
                    return 0
                }
                if (debit) {
                    return Number(data.amount?.replace(',', '.'))
                }
                if (credit) {
                    return Number(data.amount?.replace(',', '.')) * -1
                }
            },
            cellClass: 'text-right',
            cellRenderer: (params: ICellRendererParams) => formatCurrency(params.value),
            aggFunc: 'sum'
        },
        {
            headerName: 'Per.konto',
            field: 'accrAccount'
        },
        {
            headerName: 'Per.start år',
            field: 'accrStartYear'
        },
        {
            headerName: 'Per.start mnd',
            field: 'accrStartMonth'
        },
        {
            headerName: 'Per. varighet',
            field: 'accrDuration'
        }
    ]
})

const convertCsvToGridRows = () => {
    const {data: csv}: { data: string[][] } = Papa.parse(props.data as string)
    const data = csv.filter(row => !!row && row[0] !== '')
    if (!data || !data.length) {
        console.warn('Unable to parse data from pasted content', props.data)
        //TODO: Notify
        return
    }
    switch (importFormat.value) {
        case CsvFormats.VIPPS: {
            rowData.value = vippsMapper(data, importConfig.value)
            break
        }
        case CsvFormats.KLARNA: {
            rowData.value = klarnaMapper(data, importConfig.value)
            break
        }
        case CsvFormats.CUSTOM: {
            rowData.value = customRowMapper(data)
            break
        }
    }
}

onMounted(convertCsvToGridRows)

const readLines = () => {
    writeFieldListToTransactionEntries(rowData.value)
    emit('close')
}

watch(importConfig.value, () => {
    convertCsvToGridRows()
})

</script>

<style scoped>

</style>